import { useContext, useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { getSamplePrompts } from '../../services/savedPrompts';
import { LoadingContext } from '../../context/LoadingContext';
import { PromptContext } from '../../context/PromptContext';
import ChatIcon from '../../assets/icon-sample-chat.svg?react';
import { HISTORY_ZERO_STATE } from '../../constants/HistoryConstants';
import { generateChatSessionId } from '../../utils/chat';
import { SettingContext } from '@/context/SettingContext';
import useElysiaStore from '@/app/elysia-store';
import LoadingPageComponent from '../common/LoadingPage';

interface SamplePrompt {
  prompt: string;
  prompt_id: number;
}

export const SamplePrompts = () => {
  const navigate = useNavigate();
  const [sampleTopics, setSampleTopics] = useState<SamplePrompt[]>([]);
  const { isLoading } = useContext(LoadingContext);
  const [isLoadingPrompts, setIsLoadingPrompts] = useState<boolean>(false);
  const { setPrompt, setProcessed } = useContext(PromptContext);
  const { selectedLanguage, selectedModel, languageUsedForResponse, modelUsedForResponse } =
    useContext(SettingContext);

  const starterPrompts = useElysiaStore((state) => state.starterPrompts);
  const isAppPreview = useElysiaStore((state) => state.isAppPreview);
  const chatAgent = useElysiaStore((state) => state.chatAgent);
  const setPromptInput = useElysiaStore.getState().setPromptInput;
  const setChatSessionId = useElysiaStore.getState().setChatSessionId;
  const setNewChat = useElysiaStore.getState().setNewChat;
  const setClearState = useElysiaStore.getState().setClearState;
  const setChatAgent = useElysiaStore.getState().setChatAgent;
  const setActiveChatHistoryRecord = useElysiaStore.getState().setActiveChatHistoryRecord;
  const setSessionPath = useElysiaStore.getState().setSessionPath;
  const setIsRestrictedChatSessionStatus =
    useElysiaStore.getState().setIsRestrictedChatSessionStatus;
  const setIsStreamingRequested = useElysiaStore.getState().setIsStreamingRequested;

  const { pathname } = useLocation();

  const starterPromptsObject = starterPrompts?.map((item, idx) => {
    return { prompt: item, prompt_id: idx };
  });

  const startNewSavedPromptChat = (promptValue) => {
    useElysiaStore.setState({ isStreamingStopped: false });
    setIsStreamingRequested?.(true);
    setClearState?.(true);
    setNewChat?.(true);
    setChatSessionId?.(generateChatSessionId());
    if (chatAgent !== 'app-agent') {
      setChatAgent?.('Generic');
    }
    setActiveChatHistoryRecord(HISTORY_ZERO_STATE);
    if (!isAppPreview) {
      navigate('/');
    }
    setPromptInput('');
    setSessionPath?.('');
    setPrompt?.(promptValue);
    setProcessed?.(false);
    setIsRestrictedChatSessionStatus?.('');
    languageUsedForResponse.current = selectedLanguage;
    modelUsedForResponse.current = selectedModel;
  };

  const chatAgentRef = useRef(chatAgent);

  // Keep ref in sync with the latest chatAgent value
  useEffect(() => {
    chatAgentRef.current = chatAgent;
  }, [chatAgent]);

  // generic starter prompts
  useEffect(() => {
    if (pathname !== '/app-builder' && chatAgent === 'Generic') {
      (async () => {
        setIsLoadingPrompts(true);
        let response;
        try {
          response = await getSamplePrompts();
        } catch (err) {
          console.error('error fetching sample prompts', err);
        } finally {
          setIsLoadingPrompts(false);
        }
        if (response) {
          // set sample prompts only for generic chat and not app agents
          if (chatAgentRef.current === 'Generic') {
            setSampleTopics(response);
          }
        }
      })();
    } else if (chatAgent === 'app-agent') {
      setSampleTopics(starterPromptsObject);
    }
    //eslint-disable-next-line
  }, [starterPrompts, chatAgent]);

  return (
    <>
      {isLoadingPrompts ? (
        <LoadingPageComponent />
      ) : (
        <div className="nav-box">
          {sampleTopics
            ?.filter((topic) => Boolean(topic?.prompt))
            ?.map((topic, index) => (
              <Button
                disabled={isLoading}
                key={index}
                className="sample-prompt-card"
                title={topic.prompt}
                onClick={() => {
                  startNewSavedPromptChat(topic.prompt);
                }}
              >
                <div className="d-flex sample-prompt-card-body">
                  <ChatIcon className="chat-save-icon" />
                  <div className="sample-prompt-text">{topic.prompt}</div>
                </div>
              </Button>
            ))}
        </div>
      )}
    </>
  );
};
